import React, { useEffect, useRef, useState } from 'react';

const CYCLES = 3; // turns of the names before the reel lands
const ARM_MS = 50; // the reel is drawn at the top first, so the transition has somewhere to go
const DEFAULT_DURATION = 6000;
const FIT_CHARS = 10; // names up to this long show at full size; longer ones shrink to fit the reel

export const DEFAULT_TITLE = 'Jackpot de clientes';

// The strip behind the reel: the names, several turns of them, ending on the winner
const stripFor = ({ names, winner }) => {
  const sample = names?.length ? names : [winner];
  return [...Array(CYCLES).fill(sample).flat(), winner];
};

/**
 * The clients' jackpot: one wide reel that runs through the names of the people who bought
 * and stops on the winner. It replaces a wheel, which with a hundred buyers showed nothing
 * anyone could read. `showTotal` adds among how many clients the draw was: the panel wants
 * it, the phones do not.
 */
const BuyersJackpot = ({ draw, participant, showTotal = false }) => {
  const [phase, setPhase] = useState('idle'); // idle | spinning | revealed
  const [armed, setArmed] = useState(false); // the reel sits at its landing position
  const drawId = useRef(null);
  const timers = useRef([]);

  useEffect(() => () => timers.current.forEach(clearTimeout), []);

  const duration = draw?.duration || DEFAULT_DURATION;

  useEffect(() => {
    if (!draw || draw.id === drawId.current) return;
    drawId.current = draw.id;
    timers.current.forEach(clearTimeout);
    timers.current = [];

    // A winner recovered from the history (a late joiner) is shown without animating
    if (!draw.live) {
      setPhase('revealed');
      setArmed(true);
      return;
    }

    setPhase('spinning');
    setArmed(false);
    timers.current.push(setTimeout(() => setArmed(true), ARM_MS));
    timers.current.push(setTimeout(() => setPhase('revealed'), duration));
  }, [draw, duration]);

  const spinning = phase === 'spinning';
  const revealed = phase === 'revealed' && Boolean(draw);
  const youWon = revealed && participant?.nickname === draw.winner;

  const strip = phase === 'idle' ? ['?'] : draw.live ? stripFor(draw) : [draw.winner];
  const position = armed ? strip.length - 1 : 0;
  const longest = Math.max(...strip.map((name) => name.length));

  return (
    <section className='jackpot jackpot--names'>
      <h2 className='stage__title'>{draw?.title || DEFAULT_TITLE}</h2>

      {/* A new draw remounts the reel, so it starts again from the top */}
      <div key={draw?.id || 'idle'} className={`jackpot__machine${revealed ? ' jackpot__machine--hit' : ''}`}>
        <div className='jackpot__reel jackpot__reel--name' style={{ '--name-scale': Math.min(1, FIT_CHARS / longest) }}>
          <div
            className='jackpot__strip'
            style={{
              transform: `translateY(calc(${-position} * var(--digit)))`,
              transition: spinning && armed ? `transform ${duration}ms cubic-bezier(0.12, 0.8, 0.2, 1)` : 'none',
            }}
          >
            {strip.map((name, at) => (
              <span key={at} className='jackpot__name'>
                {name}
              </span>
            ))}
          </div>
        </div>
      </div>

      {revealed ? (
        <div className={`stage__result${youWon ? ' stage__result--you' : ''}`}>
          <p className='stage__winner'>🎉 {draw.winner}</p>
          <p className='stage__caption'>
            {youWon ? '¡Ganaste vos! Acercate al escenario' : showTotal ? `Ganó` : 'Ganó el jackpot de clientes'}
          </p>
        </div>
      ) : (
        <p className='stage__caption'>
          {spinning
            ? showTotal
              ? `Sorteando`
              : 'Sorteando entre los clientes…'
            : 'El sorteo arranca desde el escenario. Si compraste, ya estás participando.'}
        </p>
      )}
    </section>
  );
};

export default BuyersJackpot;
